import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { EmptyState, Skeleton } from './Feedback';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
  /** Sembunyikan di kartu mobile — biasanya kolom yang sudah masuk judul. */
  hideOnCard?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: { title: string; description?: string; action?: ReactNode };
  /** Judul kartu di tampilan mobile. */
  cardTitle: (row: T) => ReactNode;
  cardSubtitle?: (row: T) => ReactNode;
  cardBadge?: (row: T) => ReactNode;
  /**
   * Aktifkan kolom ceklist. Dikendalikan dari luar supaya halaman pemanggil
   * yang memegang pilihan — pilihan harus bertahan saat daftar dimuat ulang,
   * dan hilang saat filter berubah. Keduanya keputusan halaman, bukan tabel.
   */
  selection?: {
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
  };
}

/**
 * Di bawah 1024px tabel berubah menjadi daftar kartu — BUKAN tabel yang
 * di-scroll horizontal. Tabel lebar di layar sempit praktis tidak terbaca,
 * dan PRD menempatkan mobile sebagai perangkat utama.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  empty,
  cardTitle,
  cardSubtitle,
  cardBadge,
  selection,
}: Props<T>) {
  const pageIds = rows.map(rowKey);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selection?.selected.has(id));

  function toggle(id: string) {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  }

  /** Hanya membalik baris di halaman ini — pilihan di halaman lain tidak disentuh. */
  function toggleAllOnPage() {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    selection.onChange(next);
  }

  if (loading) return <TableSkeleton cols={columns.length} />;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon="🔎"
        title={empty?.title ?? 'Belum ada data'}
        description={empty?.description}
        action={empty?.action}
      />
    );
  }

  return (
    <>
      {/* Desktop */}
      <table className="hidden w-full border-collapse text-left lg:table">
        <thead>
          <tr className="border-b border-border">
            {selection && (
              <th scope="col" className="w-12 px-4 py-3">
                <Checkbox
                  checked={allOnPageSelected}
                  onChange={toggleAllOnPage}
                  label="Pilih semua baris di halaman ini"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'px-4 py-3 text-[13px] font-semibold text-ink-secondary',
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = rowKey(row);
            const checked = selection?.selected.has(id) ?? false;

            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-border last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-app',
                  checked && 'bg-primary-soft/40',
                )}
              >
                {selection && (
                  // Klik di sel ceklist tidak boleh ikut membuka halaman detail.
                  <td className="px-4 py-3.5 align-middle" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggle(id)}
                      label={`Pilih baris ini`}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={cn('px-4 py-3.5 align-middle', col.className)}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile */}
      {selection && rows.length > 0 && (
        <div className="mb-2.5 flex lg:hidden">
          <Checkbox
            checked={allOnPageSelected}
            onChange={toggleAllOnPage}
            label="Pilih semua di halaman ini"
            showLabel
          />
        </div>
      )}

      <ul className="flex flex-col gap-2.5 lg:hidden">
        {rows.map((row) => {
          const id = rowKey(row);
          const checked = selection?.selected.has(id) ?? false;

          // Kartu tidak lagi satu <button> besar: checkbox di dalam button
          // adalah HTML tidak sah dan tidak bisa diklik terpisah. Sekarang
          // checkbox berdiri sendiri, isi kartunya yang jadi tombol.
          return (
            <li
              key={id}
              className={cn(
                'flex items-start gap-3 rounded-[var(--radius-input)] border p-4',
                checked ? 'border-primary bg-primary-soft/40' : 'border-border',
              )}
            >
              {selection && (
                <div className="pt-0.5">
                  <Checkbox checked={checked} onChange={() => toggle(id)} label="Pilih data ini" />
                </div>
              )}

              <button
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                disabled={!onRowClick}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{cardTitle(row)}</p>
                    {cardSubtitle && (
                      <p className="mt-0.5 text-[13px] text-ink-secondary">{cardSubtitle(row)}</p>
                    )}
                  </div>
                  {cardBadge?.(row)}
                </div>

                <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px]">
                  {columns
                    .filter((col) => !col.hideOnCard)
                    .map((col) => (
                      <div key={col.key} className="flex items-center gap-1.5">
                        <dt className="text-ink-muted">{col.header}:</dt>
                        <dd className="text-ink">{col.render(row)}</dd>
                      </div>
                    ))}
                </dl>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * Kotak ceklist. Input aslinya tetap ada (sr-only) supaya keyboard, screen
 * reader, dan label tetap bekerja — yang digambar ulang hanya tampilannya.
 * Target sentuhnya 44px sesuai PRD, meski kotaknya sendiri terlihat 18px.
 */
function Checkbox({
  checked,
  onChange,
  label,
  showLabel = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  showLabel?: boolean;
}) {
  return (
    // min-h-11 = 44px, batas target sentuh di PRD. Kotaknya sendiri 18px agar
    // tidak mendominasi baris; sisanya area sentuh tak terlihat di sekelilingnya.
    <label className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 pr-2 select-none">
      <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
      <span
        aria-hidden
        className={cn(
          'grid size-[18px] shrink-0 place-items-center rounded-[5px] border-2 transition-colors',
          'peer-focus-visible:shadow-[0_0_0_3px_rgb(0_102_255_/_0.25)]',
          checked ? 'border-primary bg-primary text-white' : 'border-border bg-surface',
        )}
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M2.5 6.5L5 9l4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className={cn('text-sm text-ink-secondary', !showLabel && 'sr-only')}>{label}</span>
    </label>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="flex flex-col gap-2 p-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className="h-9 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  if (total === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
      <p className="text-[13px] text-ink-secondary">
        Halaman {page} dari {totalPages} · {total} data
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="min-h-9 rounded-lg border border-border px-3 text-sm disabled:opacity-40"
        >
          ‹ Sebelumnya
        </button>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="min-h-9 rounded-lg border border-border px-3 text-sm disabled:opacity-40"
        >
          Berikutnya ›
        </button>
      </div>
    </div>
  );
}
